/**
 * Who a work note came from, carried as a tag at the start of the note text.
 *
 * ServiceNow's `ticket_details` gives a note only a `date` and a `value` (checked on nsctest, 2026-10-19: no author
 * field on any of 40 entries), so the app cannot ask who wrote a note. Instead every comment an engineer leaves from
 * SmartTeams starts with `[From Smartteams - <full name>]`, in the same style ServiceNow uses for notes that come
 * from a work order (`[From Work Order WO0022144]`). The tag is written by {@link smartTeamsNoteTag} and recognised by
 * {@link isSmartTeamsNote}, which keeps the two in one place.
 *
 * Why it matters: the app shows only the latest note of a ticket. If that note is the engineer's own comment it
 * hides the one that was actually addressed to them (say, an instruction to cancel), so notes from SmartTeams are
 * left out of what is shown.
 *
 * Pure, so it can be tested without ServiceNow.
 */

const SOURCE = 'Smartteams';

/** Shown when the engineer's name cannot be resolved, so the tag is always present and always recognisable. */
const UNKNOWN_ENGINEER = 'unknown engineer';

/** `[From Smartteams - Jane Doe]`. Brackets and line breaks in the name are dropped so the tag stays well formed. */
export function smartTeamsNoteTag(engineerName: string | null | undefined): string {
  const name = (engineerName ?? '')
    .replace(/[[\]\r\n]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
  return `[From ${SOURCE} - ${name || UNKNOWN_ENGINEER}]`;
}

const SMARTTEAMS_TAG = /^\s*\[from smartteams\s*-[^\]]*\]/i;

/** Whether the note text (without markup) starts with the SmartTeams tag. */
export function isSmartTeamsNote(text: string | null | undefined): boolean {
  return SMARTTEAMS_TAG.test(text ?? '');
}
